/**
 * Runner application pipeline chips — the onboarding order Rod set 2026-10-01.
 * Palette follows the vendor chips (light tint bg + strong fg).
 */
/**
 * 🪤 This used to be a SECOND hand-maintained copy of the status union, and the
 * two drifted the first time a status was added: adding `checkr_waived` to the
 * API client compiled fine here and failed only where the two types met. One
 * source of truth instead — adminApiClient does not import this module, so the
 * dependency runs one way and cannot become circular.
 */
export type { RunnerAppStatus } from './adminApiClient';
import type { RunnerAppStatus } from './adminApiClient';

export interface RunnerStageDef {
  status: RunnerAppStatus;
  label: string;
  /** Short label for the stepper. */
  short: string;
  fg: string;
  bg: string;
}

/** The linear happy path shown in the stepper (§5 stages 1–11). */
export const RUNNER_PIPELINE: RunnerStageDef[] = [
  { status: 'submitted', label: 'Submitted', short: 'Applied', fg: '#475569', bg: '#f1f5f9' },
  { status: 'in_review', label: 'Staff review', short: 'Review', fg: '#1d4ed8', bg: '#dbeafe' },
  { status: 'profile_setup', label: 'Profile and vehicle', short: 'Profile', fg: '#0f766e', bg: '#ccfbf1' },
  { status: 'insurance_review', label: 'Insurance', short: 'Insurance', fg: '#0f766e', bg: '#ccfbf1' },
  { status: 'training_ack', label: 'Handbook', short: 'Handbook', fg: '#0f766e', bg: '#ccfbf1' },
  { status: 'verification_call', label: 'Video call', short: 'Call', fg: '#1d4ed8', bg: '#dbeafe' },
  { status: 'checkr_pending', label: 'Checkr pending', short: 'Checkr', fg: '#7c3aed', bg: '#ede9fe' },
  { status: 'checkr_clear', label: 'Checkr clear', short: 'Cleared', fg: '#15803d', bg: '#dcfce7' },
  { status: 'banking_tax', label: 'Banking & tax', short: 'Banking', fg: '#0f766e', bg: '#ccfbf1' },
  { status: 'pre_golive_review', label: 'Final review', short: 'Final review', fg: '#1d4ed8', bg: '#dbeafe' },
  { status: 'approved', label: 'Approved', short: 'Approved', fg: '#15803d', bg: '#dcfce7' },
  { status: 'live', label: 'Live', short: 'Live', fg: '#ffffff', bg: '#2E9E3A' },
];

export const RUNNER_OFF_PATH: RunnerStageDef[] = [
  // Not a failure — an ALTERNATE ROUTE THROUGH THE SAME RUNG as checkr_clear,
  // which is why it lives here rather than in the linear stepper: putting it
  // there would add a phantom step to every applicant's progress rail.
  // runnerStageIndex maps it onto the clear's position.
  //
  // 🛑 AMBER, never the green used for a real clear. No consumer report was
  // ever ordered for a waived applicant. If this chip ever reads as "passed",
  // someone six months from now will assume every live Runner was checked.
  { status: 'checkr_waived', label: 'Check waived', short: 'Waived', fg: '#7A4A08', bg: '#FDF3E1' },
  { status: 'checkr_consider', label: 'Checkr — consider', short: 'Consider', fg: '#b45309', bg: '#fef3c7' },
  { status: 'declined_prescreen', label: 'Declined (pre-screen)', short: 'Declined', fg: '#b91c1c', bg: '#fee2e2' },
  { status: 'declined_checkr', label: 'Declined (post-Checkr)', short: 'Declined', fg: '#b91c1c', bg: '#fee2e2' },
  { status: 'withdrawn', label: 'Withdrawn', short: 'Withdrawn', fg: '#64748b', bg: '#f1f5f9' },
  // Dropped 2026-10-01; only an older application can still be here.
  { status: 'shadow_ride', label: 'Shadow ride (retired)', short: 'Shadow', fg: '#64748b', bg: '#f1f5f9' },
];

export const RUNNER_TERMINAL: RunnerAppStatus[] = ['declined_prescreen', 'declined_checkr', 'live', 'withdrawn'];

export function runnerStageChip(status: string): RunnerStageDef {
  return (
    [...RUNNER_PIPELINE, ...RUNNER_OFF_PATH].find((s) => s.status === status) ?? {
      status: status as RunnerAppStatus,
      label: status,
      short: status,
      fg: '#475569',
      bg: '#f1f5f9',
    }
  );
}

export function runnerStageIndex(status: string): number {
  if (status === 'checkr_consider') return RUNNER_PIPELINE.findIndex((s) => s.status === 'checkr_pending');
  // Waived sits at the clear's position on the progress rail: same rung, and
  // the chip beside it is what says HOW they got there.
  if (status === 'checkr_waived') return RUNNER_PIPELINE.findIndex((s) => s.status === 'checkr_clear');
  if (status === 'shadow_ride') return RUNNER_PIPELINE.findIndex((s) => s.status === 'pre_golive_review');
  return RUNNER_PIPELINE.findIndex((s) => s.status === status);
}

export const TRANSPORT_LABEL: Record<string, string> = {
  car: 'Car', suv_van: 'SUV / van', bike: 'Bike', ebike: 'E-bike', scooter: 'Scooter', on_foot: 'On foot',
};
export const METRO_LABEL: Record<string, string> = { charlotte: 'Charlotte', cincinnati: 'Cincinnati' };

/**
 * Checkr's SMB display column, verbatim. Shown beside the pipeline stage —
 * a suspended or disputed report moves neither `status` nor `checkrResult`,
 * and without this line the reviewer sees a stalled applicant and no reason.
 * 🔑 Never the report contents: Checkr recommends against rendering them in
 * our app. The link goes to their dashboard.
 */
export const CHECKR_STATUS_LABEL: Record<string, string> = {
  invitation_sent: 'Invitation sent',
  pending: 'Pending',
  invitation_expired: 'Invitation expired',
  invitation_canceled: 'Invitation canceled',
  clear: 'Clear',
  canceled: 'Canceled',
  clear_with_canceled: 'Clear w/ canceled screenings',
  needs_review: 'Needs review',
  pre_adverse: 'Pre-adverse action sent',
  not_eligible: 'Not eligible (post-adverse)',
  suspended: 'Suspended — candidate must resolve with Checkr',
  disputed: 'Disputed',
};
export function checkrStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return CHECKR_STATUS_LABEL[status] ?? status;
}
